import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import FoundingBanner from "@/components/FoundingBanner";
import { ArrowRight } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleSignup() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      await signup(name, email, password);
      navigate("/assessment", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 lg:p-16 bg-[#0A0A0A] order-2 lg:order-1">
        <div className="w-full max-w-md">
          <div className="text-overline mb-4">Get Started</div>
          <h1 className="font-display text-3xl lg:text-4xl font-medium tracking-tight mb-2">Create your BeatCredsi account.</h1>
          <p className="text-neutral-400 mb-6">Free assessment. No card required for Phase 1.</p>

          <div className="mb-8">
            <FoundingBanner variant="compact" />
          </div>

          <button data-testid="google-signup-btn" onClick={googleSignup}
            className="w-full inline-flex items-center justify-center gap-3 py-3 border border-white/15 hover:border-amber-500/60 transition-colors mb-6">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.2-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.7 6.5 29.1 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.7 7 29.1 5 24 5 16.3 5 9.7 9.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 43c5 0 9.6-1.9 13-5l-6-5c-2 1.4-4.5 2.2-7 2.2-5.3 0-9.6-3.1-11.2-7.5l-6.5 5C9.6 38.5 16.2 43 24 43z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.3 4.4-4.3 5.8l6 5c-.4.3 6.5-4.7 6.5-14.8 0-1.2-.1-2.3-.4-3.5z"/></svg>
            <span className="text-sm font-medium">Continue with Google</span>
          </button>

          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-neutral-500 uppercase tracking-widest">or sign up with email</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
            <div>
              <label className="text-xs text-neutral-400 uppercase tracking-widest mb-2 block">Name</label>
              <input data-testid="signup-name-input" required value={name} onChange={(e) => setName(e.target.value)}
                className="w-full bg-[#161616] border border-white/20 px-4 py-3 text-white outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/40 transition-colors" />
            </div>
            <div>
              <label className="text-xs text-neutral-400 uppercase tracking-widest mb-2 block">Email</label>
              <input data-testid="signup-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#161616] border border-white/20 px-4 py-3 text-white outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/40 transition-colors" />
            </div>
            <div>
              <label className="text-xs text-neutral-400 uppercase tracking-widest mb-2 block">Password (min 8)</label>
              <input data-testid="signup-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#161616] border border-white/20 px-4 py-3 text-white outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/40 transition-colors" />
            </div>
            {error && <div data-testid="signup-error" className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 px-3 py-2">{error}</div>}
            <button data-testid="signup-submit-btn" type="submit" disabled={busy}
              className="w-full bg-amber-500 text-black font-semibold py-3 hover:brightness-110 transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? "Creating…" : <>Create Account <ArrowRight size={16} /></>}
            </button>
            <p className="text-[13px] text-neutral-500 leading-relaxed text-center" data-testid="signup-legal-notice">
              By creating an account you agree to BeatCredsi's{" "}
              <Link to="/terms" className="text-amber-500 hover:underline">Terms of Service</Link>,{" "}
              <Link to="/privacy" className="text-amber-500 hover:underline">Privacy Policy</Link>,{" "}
              <Link to="/refunds" className="text-amber-500 hover:underline">Refund Policy</Link>,{" "}
              <Link to="/cookies" className="text-amber-500 hover:underline">Cookie Policy</Link>, and{" "}
              <Link to="/aup" className="text-amber-500 hover:underline">Acceptable Use Policy</Link>.
            </p>
          </form>

          <div className="mt-6 text-sm text-neutral-400">
            Already have an account? <Link to="/login" data-testid="login-link" className="text-amber-500 hover:underline">Sign in</Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative bg-[#0A0A0A] grain order-1 lg:order-2">
        <img src="https://images.pexels.com/photos/5749190/pexels-photo-5749190.jpeg?auto=compress&w=1400&q=70"
             alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-bl from-[#0A0A0A]/30 via-[#0A0A0A]/70 to-[#0A0A0A]" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="font-display font-extrabold text-xl">BeatCredsi<span className="text-amber-500 text-sm align-super">™</span></Link>
          <div>
            <div className="text-overline mb-4">22 Questions · ~5 Minutes</div>
            <h2 className="font-display text-4xl lg:text-5xl font-medium leading-tight max-w-md">
              First step: <br /><span className="text-amber-500">your BeatCredsi Score.</span>
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
}
